import {
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material'
import { SpeciesResearchTypes } from '@/features/projectDetail/SpeciesResearchTypes'
import { AuthorizationGuard, LinkButton } from '@/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useMemo } from 'react'
import { ResearchArea, ResearchVisitEvent, UserGroups } from '@/types'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DATE_FORMAT_DMY, ROUTES_PATH, PageReferrers } from '@/constants'
import { capitalizeFirstLetter, getPath } from '@/utils'
import { theme } from '@/theme'

export type ResearchAreaEventProps = {
  isLoading?: boolean
  events: ResearchVisitEvent[]
  researchArea: ResearchArea
  canGoToDetail?: boolean
}

export const ResearchAreaEvents = ({
  isLoading = false,
  events,
  researchArea,
  canGoToDetail = false,
}: ResearchAreaEventProps) => {
  const { t } = useTranslation('projects')

  const protocolEvents = useMemo<Record<number, ResearchVisitEvent[]>>(() => {
    const values: Record<number, ResearchVisitEvent[]> = {}

    events.forEach((event) => {
      const { protocol_id } = event

      if (!values[protocol_id] || !values[protocol_id].length) {
        values[protocol_id] = []
      }

      values[protocol_id].push(event)
    })

    return values
  }, [events])

  return (
    <Stack spacing={3} mt={5}>
      {isLoading && <Skeleton height={125} />}

      <Stack spacing={3}>
        {researchArea?.research_area_protocols.map(
          ({ protocol, area_protocol_researchers }) => (
            <Stack
              key={protocol.id}
              data-testid="research-type-events-form"
              p={5}
              bgcolor="grey.50"
              borderRadius={2.5}
              spacing={5}
              flex={1}
              width="100%"
            >
              <SpeciesResearchTypes
                protocol={protocol}
                speciesColumnWidth="240px"
                researchTypesColumnWidth="100%"
              />
              <AuthorizationGuard
                allowedGroups={[UserGroups.MANAGER, UserGroups.EMPLOYEE]}
                redirect={false}
              >
                {/* TODO: Planning control checks are temporarily disabled. Uncomment to re-enable.
                {protocol.required_checks?.length > 0 && (
                  <Stack>
                    <Typography
                      variant="caption"
                      color="grey.600"
                      lineHeight="16px"
                    >
                      {t('protocolsStep.planningControlChecks')}
                    </Typography>
                    {protocol.required_checks.map((rch, index) => (
                      <Typography key={index}>
                        {t(`forms:planningControlChecks.${rch}`)}
                      </Typography>
                    ))}
                  </Stack>
                )}
                */}
                {area_protocol_researchers.length > 0 && (
                  <Stack>
                    <Typography
                      variant="caption"
                      color="grey.600"
                      lineHeight="16px"
                    >
                      {t('protocolsStep.roundRequiredResearchersNumber')}
                    </Typography>
                    {area_protocol_researchers.map((apr, index) => (
                      <Typography key={index}>
                        {capitalizeFirstLetter(apr.round_type)}:&nbsp;
                        {apr.number_of_researchers}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </AuthorizationGuard>
              <Stack
                px={5}
                mb={1}
                borderRadius="12px"
                spacing={4}
                direction="row"
                flex={1}
                justifyContent="space-between"
              />
              <Stack spacing={5}>
                {!protocolEvents[protocol.id]?.length ? (
                  <Typography>{t('areas.noEvents')}</Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      filter: 'drop-shadow(0px 4px 5px rgba(0, 0, 0, 0.07))',
                    }}
                  >
                    <Table stickyHeader={true}>
                      <TableHead
                        sx={{
                          borderRadius: 8,
                        }}
                      >
                        <TableRow>
                          <TableCell
                            sx={{
                              backgroundColor: theme.palette.green[200],
                            }}
                          >
                            {t(`areas.round`)}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: theme.palette.green[200],
                            }}
                          >
                            {t(`areas.startDate`)}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: theme.palette.green[200],
                            }}
                          >
                            {t(`areas.researchers`)}
                          </TableCell>
                          <TableCell
                            sx={{
                              backgroundColor: theme.palette.green[200],
                            }}
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {protocolEvents[protocol.id]?.map(
                          ({
                            id,
                            round_name,
                            start_datetime,
                            researchers,
                            cloned_from,
                            reasons_of_uncertainty,
                          }) => (
                            <TableRow key={id}>
                              <TableCell>
                                {reasons_of_uncertainty &&
                                  reasons_of_uncertainty.length > 0 && (
                                    <Tooltip
                                      title={t('areas.hasUncertainties')}
                                    >
                                      <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        color="red"
                                        style={{
                                          fontSize: '16px',
                                          marginRight: '8px',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                {round_name}&nbsp;
                                {!!cloned_from &&
                                  t(`titles.cloned`, {
                                    id: cloned_from,
                                  })}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {start_datetime
                                  ? dayjs(start_datetime).format(
                                      DATE_FORMAT_DMY
                                    )
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {researchers?.map(
                                  ({ first_name, last_name }, i) =>
                                    `${
                                      i > 0 ? ', ' : ''
                                    }${first_name} ${last_name}`
                                ) ?? '-'}
                              </TableCell>
                              <TableCell>
                                <AuthorizationGuard
                                  allowedGroups={[UserGroups.MANAGER]}
                                  redirect={false}
                                >
                                  {canGoToDetail && (
                                    <LinkButton
                                      url={getPath(
                                        ROUTES_PATH.MONITORING_DETAIL,
                                        {
                                          id,
                                        }
                                      )}
                                      title={t(`buttons.detail`)}
                                      referrer={PageReferrers.PROJECT}
                                      arrowIcon={true}
                                    />
                                  )}
                                </AuthorizationGuard>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Stack>
          )
        )}
      </Stack>
    </Stack>
  )
}
